import React, { useEffect, useState, useMemo, useRef } from "react";
import { MobileDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { FileDownload } from "@mui/icons-material";

import * as XLSX from "xlsx";
import { fitColumns } from "../../../utils/excelFit";
import {
  Box,
  Grid,
  Button,
  Tooltip,
  TextField,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  DialogActions,
  Autocomplete,
  Snackbar,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import moment from "moment";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import { useParams } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import { useNavigate } from "react-router-dom";

import { MaterialReactTable } from "material-react-table";
import { apiData } from "../../../services/api_service";
import dayjs from "dayjs";

import {
  fetchProductDropdownAPI,
  GetProductSalesReportAPI,
  showPurchaseBreakdownAPI,
} from "../../../config";
import Layout from "../../../layout/Layout";
import { AlertMessage } from "../../../components/AlertMessage";
import { formatNumber, formatAED } from "../../../utils/format";
import { buildExportFileName } from "../../../utils/exportFileName";
import { writeXlsxFrozen } from "../../../utils/xlsxFreeze";

const ProductSalesReports = () => {
  const [tblList, setTblList] = useState([]);

  const [store, setStore] = useState(null);

  const [selectedData, setSelectedData] = useState([]);
  const [summary, setSummary] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  const { productId } = useParams();

  const [selectedProductId, setSelectedProductId] = useState(productId);

  const [message, setMessage] = useState(null);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  // Set only by Search/Clear — what's actually applied to the table right
  // now, as opposed to whatever's sitting typed in the date pickers. Export
  // and the auto-refetch effect below both read these, not fromDate/toDate
  // directly, so neither one silently drops or exports a stale date filter.
  const [selectedFromDate, setSelectedFromDate] = useState(null);
  const [selectedToDate, setSelectedToDate] = useState(null);

  const [rowCount, setRowCount] = useState(0);

  const [loading, setLoading] = useState(true);
  const [exportData, setExportData] = useState([]);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [totalBreakdownOpen, setTotalBreakdownOpen] = useState(false);
  const [totalData, setTotalData] = useState([]);
  const [breakdownData, setBreakdownData] = useState({
    transactions: [],
    total: 0,
    breakdownText: "",
  });

  const childRef = useRef();

  const [successMessage, setSuccessMessage] = useState("");
  const [productList, setProductList] = useState([]);

  const totalLineItems = Array.isArray(totalData?.line_items)
    ? totalData.line_items
    : [];
  const hasLineItemDiscount = totalLineItems.some(
    (item) => Number(item.discount_amount) > 0
  );

  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => {
      setMessage(null);
    }, 3000);
  }, [message]);

  const fetchProducts = async (search = "") => {
    try {
      const result = await apiData(
        "get",
        `${fetchProductDropdownAPI}?search=${search}`
      );

      if (result.success) {
        setProductList(result.data.result);
      }
    } catch (error) {
      console.error("Failed to fetch units:", error);
    }
  };

  const handleExportData = async () => {
    try {
      setLoading(true);

      const payload = {
        pagination: JSON.stringify({
          pageIndex: 0,
          pageSize: 10000000,
        }),
        fromDate: selectedFromDate ? dayjs(selectedFromDate).format("YYYY-MM-DD") : null,
        toDate: selectedToDate ? dayjs(selectedToDate).format("YYYY-MM-DD") : null,
        sorting: sorting.length > 0 ? JSON.stringify(sorting) : null,
        search: columnFilters.length > 0 ? JSON.stringify(columnFilters) : null,
        globalSearch: globalFilter || null,
        store_id: store || null,
        // The currently-applied/displayed product, not the original URL
        // param — the dropdown above lets it be switched without navigating.
        product_id: selectedProductId,
      };

      const response = await apiData("get", GetProductSalesReportAPI, payload);

      if (!response?.data?.result || response.data?.result.length === 0) {
        alert("No data to export!");
        return;
      }

      // Money is written as real numbers (two decimals via the cell format below)
      // and Profit is split into its AED amount and its percentage.
      const r2 = (v) => Math.round((parseFloat(v) || 0) * 100) / 100;
      const exportData = response.data.result.map((item, index) => ({
        "#": index + 1,
        "Invoice No.": item.order_number || "",
        "Order Date": item.order_date ? moment(item.order_date).format("DD-MM-YYYY") : "",
        "Order Placed By": item.created_by_name || "",
        "Store Name": item.store_name || "",
        "Total Sales Cost (AED)": r2(item.total_price),
        "Selling Price (AED)": r2(item.selling_price),
        "Total Quantity Sold": Number(item.quantity) || 0,
        "Total Purchase Cost (AED)": r2(item.total_buying_cost),
        "Grand Total (AED)": r2(item.grand_total),
        "Profit (AED)": r2(item.profit),
        "Profit %": r2(item.profit_percentage),
      }));

      // Total row — use the backend all-data summary (entire filtered set), the
      // same source the on-screen footer now uses, so both match exactly.
      {
        const s = response.data?.summary || {};
        const tPct = Number(s.profit_percentage) || 0;
        exportData.push({
          "Invoice No.": "Total",
          "Total Sales Cost (AED)": r2(s.total_sales),
          "Selling Price (AED)": r2(s.total_selling_price),
          "Total Quantity Sold": Number(s.total_quantity_sold) || 0,
          "Total Purchase Cost (AED)": r2(s.total_buying_cost),
          "Grand Total (AED)": r2(s.grand_total),
          "Profit (AED)": r2(s.total_profit),
          "Profit %": r2(tPct),
        });
      }

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      // Two decimals on every money / percent column (found by its header).
      const headers = Object.keys(exportData[0]);
      Object.keys(ws).forEach((addr) => {
        const m = /^([A-Z]+)(\d+)$/.exec(addr);
        if (!m || m[2] === "1" || ws[addr].t !== "n") return;
        if (/\(AED\)|Profit %/.test(headers[XLSX.utils.decode_col(m[1])] || "")) ws[addr].z = "#,##0.00";
      });
      fitColumns(ws);
      XLSX.utils.book_append_sheet(wb, ws, "Product_Sales_Report");

      const selectedProduct =
        productList.find((item) => String(item.id) === String(selectedProductId))
          ?.product_name || selectedProductId || "All_Products";
      const fileName = buildExportFileName("Product_Sales_Report", [
        { label: "Product", value: selectedProduct },
        store ? { label: "Store", value: store } : { label: "Store", value: "All_Stores" },
        selectedFromDate
          ? { label: "From", value: dayjs(selectedFromDate).format("DD-MM-YYYY") }
          : null,
        selectedToDate
          ? { label: "To", value: dayjs(selectedToDate).format("DD-MM-YYYY") }
          : null,
      ]);
      writeXlsxFrozen(XLSX, wb, fileName);
      setSuccessMessage("Excel Downloaded Successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export data.");
    } finally {
      setLoading(false);
    }
  };

  const showPurchaseBreakdown = async (order_item_id, data) => {
    try {
      const payload = { order_item_id };
      const response = await apiData("get", showPurchaseBreakdownAPI, payload);
      const transactions = response.data; // [{ quantity: 10, buying_price: 500 }, { quantity: 8, buying_price: 509 }]

      // Prepare formatted breakdown
      const breakdownText = transactions
        .map(
          (tx) =>
            `${tx.quantity} × ${formatAED(tx.buying_price)}`
        )
        .join(" + ");

      const total = transactions.reduce(
        (acc, tx) => acc + tx.quantity * tx.buying_price,
        0
      );
      setSelectedData(data);
      // Store in state
      setBreakdownData({
        transactions,
        total,
        breakdownText,
      });

      setBreakdownOpen(true); // Open dialog
    } catch (error) {
      console.error("Error fetching stock transactions:", error);
    }
  };

  async function fetchData(fromDate, toDate, selectedProductId) {
    try {
      setLoading(true);
      const payload = {
        pagination: JSON.stringify({
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
        }),
        fromDate: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : null,
        toDate: toDate ? dayjs(toDate).format("YYYY-MM-DD") : null,
        sorting: sorting.length > 0 ? JSON.stringify(sorting) : null,
        search: columnFilters.length > 0 ? JSON.stringify(columnFilters) : null,

        store_id: store || null,
        product_id: selectedProductId,
      };
      const response = await apiData("get", GetProductSalesReportAPI, payload);
      if (response.success && response.data) {
        setRowCount(response.data?.totalCount);
        setTblList(response.data.result);
        setSummary(response.data?.summary);
      }
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  }

  const handleClear = () => {
    setSelectedProductId(null);
    setFromDate(null);
    setToDate(null);
    setSelectedFromDate(null);
    setSelectedToDate(null);
    setColumnFilters([]);

    // Pass null explicitly
    fetchData(null, null, null);
  };

  const handleSearch = () => {
    setSelectedFromDate(fromDate);
    setSelectedToDate(toDate);
    fetchData(fromDate, toDate, selectedProductId);
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    // Re-applies whatever date range was last actually searched (if any) —
    // previously hardcoded to null/null here, which silently dropped an
    // applied date filter the moment a column filter, sort, or page changed.
    fetchData(selectedFromDate, selectedToDate, selectedProductId);
  }, [globalFilter, columnFilters, sorting, pagination, store]);

  // Footer totals come from the backend `summary`, which is now computed over
  // the ENTIRE filtered set (not just the current page) — so the list footer
  // matches the Excel footer. (Was a client-side reduce over the current page.)
  const pageSummary = useMemo(
    () => ({
      total_sales: Number(summary?.total_sales) || 0,
      total_selling_price: Number(summary?.total_selling_price) || 0,
      total_quantity_sold: Number(summary?.total_quantity_sold) || 0,
      total_buying_cost: Number(summary?.total_buying_cost) || 0,
      grand_total: Number(summary?.grand_total) || 0,
      total_profit: Number(summary?.total_profit) || 0,
    }),
    [summary]
  );

  const pageProfitPercentage = useMemo(
    () => Number(summary?.profit_percentage) || 0,
    [summary]
  );

  const Innercolumns = useMemo(() => {
    return [
      {
        accessorKey: "order_number",
        header: "Invoice No.",
        size: 10,
        // Clicking the invoice number opens the generated invoice PDF
        // (order_receipt) — same pattern as OrdersList's Invoice No. column.
        Cell: ({ row }) => {
          const num = row.original.order_number;
          const url = row.original.order_receipt;
          if (!num) return "-";
          return url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", fontWeight: 600, textDecoration: "none" }}
            >
              {num}
            </a>
          ) : (
            num
          );
        },
      },
      {
        accessorKey: "order_date",
        header: "Order Date",
        Cell: ({ row }) => moment(row.original.order_date).format("DD-MM-YYYY"),
        size: 10,
        // Footer: () => "TOTAL",
        Filter: ({ column }) => {
          const value = column.getFilterValue() || null;

          return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                value={value ? dayjs(value, "YYYY-MM-DD") : null}
                inputFormat="DD-MM-YYYY"
                onChange={(newValue) => {
                  if (newValue) {
                    const formatted = dayjs(newValue).format("YYYY-MM-DD");
                    column.setFilterValue(formatted);
                  } else {
                    column.setFilterValue(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="standard"
                    placeholder="Select Date"
                    sx={{
                      "& .MuiInputBase-input": {
                        color: "#fff !important",
                      },
                      "& .MuiInput-underline:before": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomColor: "#fff !important",
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#fff !important",
                        opacity: 0.7,
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {params.InputProps.endAdornment}
                          {value && (
                            <IconButton
                              size="small"
                              onClick={() => column.setFilterValue(null)}
                              sx={{ color: "#fff" }}
                            >
                              ✕
                            </IconButton>
                          )}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          );
        },
      },
      {
        accessorKey: "created_by_name",
        header: "Order Placed By",
        size: 10,
      },
      {
        accessorKey: "store_name",
        header: "Store Name",
        size: 10,
        Footer: () => "TOTAL",
      },
      {
        accessorKey: "total_price",
        header: "Total Sales Cost (AED)",
        size: 150,
        Cell: ({ row }) => (
          <Typography sx={{ textAlign: "right" }}>
            {formatAED(row.original.total_price)}
          </Typography>
        ),
        Footer: () => (
          <Typography sx={{ fontWeight: 600, textAlign: "right" }}>
            {pageSummary?.total_sales ? formatAED(pageSummary.total_sales) : 0}
          </Typography>
        ),
      },
      // {
      //   accessorKey: "buying_price",
      //   header: "Buying Price (AED)",
      //   size: 10,
      //   Cell: ({ row }) => (
      //     <Typography sx={{ textAlign: "right" }}>
      //       {parseFloat(row.original.buying_price || 0).toLocaleString(
      //         "en-AE",
      //         { minimumFractionDigits: 2 }
      //       )}
      //     </Typography>
      //   ),
      // },
      {
        accessorKey: "selling_price",
        header: "Selling Price (AED)",
        size: 10,
        Cell: ({ row }) => (
          <Typography sx={{ textAlign: "right" }}>
            {formatAED(row.original.selling_price)}
          </Typography>
        ),
        Footer: () => (
          <Typography sx={{ fontWeight: 600, textAlign: "right" }}>
            {formatAED(pageSummary?.total_selling_price)}
          </Typography>
        ),
      },
      {
        accessorKey: "quantity",
        header: "Total Quantity Sold",
        size: 10,
        Cell: ({ row }) => (
          <Typography sx={{ textAlign: "right" }}>
            {formatNumber(row.original.quantity || 0)}
          </Typography>
        ),
        Footer: () => (
          <Typography sx={{ fontWeight: 600, textAlign: "right" }}>
            {formatNumber(pageSummary?.total_quantity_sold || 0)}
          </Typography>
        ),
      },
      {
        accessorKey: "total_buying_cost",
        header: "Total Purchase Cost (AED)",
        size: 150,
        Cell: ({ row }) => {
          const handleClick = () => {
            // Call a function to fetch transaction breakdown
            showPurchaseBreakdown(row.original.order_item_id, row.original);
          };

          return (
            <Tooltip title="Click to see purchase breakdown" arrow>
              <Typography
                onClick={handleClick}
                sx={{
                  textAlign: "right",
                  cursor: "pointer",
                  color: "primary.main",
                  fontWeight: 500,
                  "&:hover": {
                    textDecoration: "underline",
                    color: "primary.dark",
                  },
                }}
              >
                {formatAED(row.original.total_buying_cost)}
              </Typography>
            </Tooltip>
          );
        },
        Footer: () => (
          <Typography sx={{ fontWeight: 600, textAlign: "right" }}>
            {pageSummary?.total_buying_cost
              ? formatAED(pageSummary.total_buying_cost)
              : 0}
          </Typography>
        ),
      },

      {
        accessorKey: "grand_total",
        header: "Grand Total (AED)",
        size: 150,
        Cell: ({ row }) => {
          const handleClick = () => {
            // Call a function to fetch transaction breakdown
            setTotalData(row.original);
            setTotalBreakdownOpen(true);
          };

          return (
            <Tooltip title="Click to see Grand total breakdown" arrow>
              <Typography
                onClick={handleClick}
                sx={{
                  textAlign: "right",
                  cursor: "pointer",
                  color: "primary.main",
                  fontWeight: 500,
                  "&:hover": {
                    textDecoration: "underline",
                    color: "primary.dark",
                  },
                }}
              >
                {formatAED(row.original.grand_total)}
              </Typography>
            </Tooltip>
          );
        },
        Footer: () => (
          <Typography sx={{ fontWeight: 600, textAlign: "right" }}>
            {pageSummary?.grand_total
              ? formatAED(pageSummary.grand_total)
              : 0}
          </Typography>
        ),
      },

      {
        accessorKey: "profit_percentage",
        header: "Profit (AED)",
        size: 100,
        Cell: ({ row }) => {
          const profit = parseFloat(row.original.profit || 0);
          const percentage = parseFloat(row.original.profit_percentage || 0);
          const isProfit = percentage >= 0;

          return (
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              gap={1}
            >
              <Typography sx={{ textAlign: "right" }}>
                {formatAED(profit)}
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                {isProfit ? (
                  <ArrowUpwardIcon
                    sx={{ fontSize: 16, color: "success.main" }}
                  />
                ) : (
                  <ArrowDownwardIcon
                    sx={{ fontSize: 16, color: "error.main" }}
                  />
                )}
                <Typography
                  sx={{ color: isProfit ? "success.main" : "error.main" }}
                >
                  {percentage.toFixed(2)}%
                </Typography>
              </Box>
            </Box>
          );
        },
        Footer: () => {
          if (!pageSummary) return "0 (0%)";
          const totalProfit = formatAED(pageSummary.total_profit);
          const profitPercent = parseFloat(pageProfitPercentage || 0);
          const isProfit = profitPercent >= 0;

          return (
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              gap={1}
              fontWeight={600}
            >
              <Typography>{totalProfit}</Typography>
              <Box display="flex" alignItems="center" gap={0.5}>
                {isProfit ? (
                  <ArrowUpwardIcon
                    sx={{ fontSize: 16, color: "success.main" }}
                  />
                ) : (
                  <ArrowDownwardIcon
                    sx={{ fontSize: 16, color: "error.main" }}
                  />
                )}
                <Typography
                  sx={{ color: isProfit ? "success.main" : "error.main" }}
                >
                  ({profitPercent.toFixed(2)}%)
                </Typography>
              </Box>
            </Box>
          );
        },
      },
    ];
  }, [loading, pageSummary, pageProfitPercentage]);

  return (
    <>
      {/* {loading ? (
        <Spinner />
      ) : ( */}
      <Layout>
        <Snackbar
          open={!!successMessage}
          autoHideDuration={3000} // hide after 3 seconds
          onClose={() => setSuccessMessage("")}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSuccessMessage("")}
            severity="success"
            sx={{
              width: "100%",
              backgroundColor: "#28a745",
              color: "#fff",
              fontWeight: 600,
            }}
          >
            {successMessage}
          </Alert>
        </Snackbar>
        <AlertMessage ref={childRef} />

        <Grid className="employee-form bold-header">
          <Grid
            container
            spacing={3}
            alignItems="center"
            justifyContent="space-between"
          >
            {/* Left section */}
            <Grid item xs={12} md={6}>
              <Typography className="page_title" textAlign="left">
                {selectedProductId
                  ? `Sales Report - ${
                      productList.find(
                        (p) => String(p.id) === String(selectedProductId)
                      )?.product_name || ""
                    }`
                  : "Sales Report - All"}
              </Typography>
              <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1 }}>
                <Link underline="hover" color="inherit" href="/dashboard">
                  Dashboard
                </Link>
                <Typography color="text.primary">Sales-Report</Typography>
              </Breadcrumbs>

              {/* {storeId && storeName !== "" && (
                <Typography textAlign="left" variant="subtitle1">
                  Store: {storeName}
                </Typography>
              )} */}
            </Grid>
            <Grid item xs={12} lg={6} md={6} textAlign="right">
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(-1)}
                startIcon={<ArrowBackIosIcon />}
                sx={{ ml: 2 }}
              >
                Back
              </Button>
            </Grid>

            <Grid container spacing={2} ml={1} mt={2} alignItems="center">
              {/* From Date */}
              <Grid item xs={12} md={4} lg={4}>
                <Autocomplete
                  fullWidth
                  options={productList}
                  getOptionLabel={(option) => option.product_name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.id == value.id
                  }
                  value={
                    productList.find((p) => p.id == selectedProductId) || null
                  }
                  onChange={(e, newValue) => {
                    // newValue is the selected product object or null if cleared
                    setSelectedProductId(newValue ? newValue.id : null);
                  }}
                  renderOption={(props, option, { selected }) => {
                    const size = option.size || "NA";
                    const color = option.color || "NA";

                    return (
                      <li
                        {...props}
                        key={option.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          padding: "6px 10px",
                          gap: 10,
                          borderBottom: "1px solid #eee",
                          backgroundColor: selected ? "#e3f2fd" : "#fff", // highlight selected
                          borderRadius: 4,
                          cursor: "pointer",
                          fontSize: 14,
                        }}
                      >
                        {/* Product Image */}
                        <img
                          src={option.product_image}
                          alt={option.product_name}
                          style={{
                            width: 36,
                            height: 36,
                            objectFit: "cover",
                            borderRadius: 6,
                            border: "1px solid #ccc",
                          }}
                        />

                        {/* Product Details */}
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            lineHeight: 1.2,
                          }}
                        >
                          <span style={{ fontWeight: 500, color: "#333" }}>
                            {option.product_name}{" "}
                            <span style={{ color: "#555", fontWeight: 400 }}>
                              {" "}
                              (SKU: {option.sku})
                            </span>
                          </span>

                          <span style={{ fontSize: 12, color: "#007BFF" }}>
                            Size: {size}, Colour: {color}
                          </span>
                        </div>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select a Product"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        //    endAdornment: (
                        //      <>
                        //        {!formik.values.items[idx].productId &&
                        //          searchTerm[idx] && (
                        //            <IconButton
                        //              size="small"
                        //              onClick={(event) => {
                        //                event.stopPropagation();
                        //                const updated = [...searchTerm];
                        //                updated[idx] = "";
                        //                setSearchTerm(updated);
                        //                fetchProducts("");
                        //              }}
                        //            >
                        //              <CloseIcon fontSize="small" />
                        //            </IconButton>
                        //          )}
                        //        {params.InputProps.endAdornment}
                        //      </>
                        //    ),
                      }}
                    />
                  )}
                  ListboxProps={{
                    style: { maxHeight: 200, overflowY: "auto" },
                  }}
                />
              </Grid>

              {/* From Date */}
              <Grid item xs={12} md={4} lg={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileDatePicker
                    label="From Date"
                    value={fromDate}
                    inputFormat="DD-MM-YYYY"
                    onChange={(newValue) => setFromDate(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                </LocalizationProvider>
              </Grid>

              {/* To Date */}
              <Grid item xs={12} md={4} lg={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileDatePicker
                    label="To Date"
                    value={toDate}
                    inputFormat="DD-MM-YYYY"
                    onChange={(newValue) => setToDate(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid
                item
                xs={12}
                container
                justifyContent="center"
                spacing={2}
                // sx={{ mt: 1 }}
              >
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    sx={{ textTransform: "none", borderRadius: "8px" }}
                  >
                    Search
                  </Button>
                </Grid>

                <Grid item>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setColumnFilters([]);
                      setFromDate(null);
                      setToDate(null);
                      handleClear();
                    }}
                    sx={{ textTransform: "none", borderRadius: "8px" }}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* Right section */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
                alignItems: "center",
              }}
            ></Grid>
          </Grid>

          <Grid mt={2}>
            <MaterialReactTable
              enableGlobalFilter={false}
              enableGlobalFiltering={false}
              columns={Innercolumns}
              data={tblList}
              enableTableFooter
              muiTableFooterProps={{
                sx: {
                  position: "sticky",
                  bottom: 0,
                  zIndex: 100,
                  backgroundColor: "#fff", // match table background
                  boxShadow: "0px -2px 8px rgba(0,0,0,0.1)",
                },
              }}
              muiToolbarAlertBannerProps={{
                sx: {
                  "& .MuiButtonBase-root": {
                    color: "red",
                    fontWeight: "500",
                    width: "100px",
                    "&:hover": {
                      background:
                        "linear-gradient(90deg, rgba(255, 153, 153, 0.8) 30%, rgba(255, 187, 187, 0.9) 100%)",
                    },
                  },
                },
              }}
              getRowId={(row) => row.id}
              initialState={{ showColumnFilters: false }}
              enableRowNumbers
              rowNumberMode="original"
              enableFullScreenToggle={false}
              enableStickyHeader
              manualFiltering
              manualPagination
              manualSorting
              onColumnFiltersChange={setColumnFilters}
              onGlobalFilterChange={setGlobalFilter}
              onPaginationChange={setPagination}
              onSortingChange={setSorting}
              rowCount={rowCount}
              muiLinearProgressProps={{
                color: "inherit",
                sx: { height: "4px" },
              }}
              state={{
                rowSelection: selectedRowIds,
                columnFilters,
                globalFilter,
                isLoading: loading,
                pagination,
                sorting,
              }}
              muiTableContainerProps={{
                sx: {
                  maxHeight: "70vh",
                  overflowY: "auto",
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  padding: "3px 8px",
                  border: "1px solid #ccc",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  padding: "8px",
                  border: "1px solid #ccc",
                  color: "#fff !important",
                  fontWeight: "bold",
                  backgroundColor: "#1976d2",
                  "& svg": {
                    color: "#fff !important",
                  },
                  "& .MuiTableSortLabel-root": {
                    color: "#fff !important",
                    "&.Mui-active": {
                      color: "#fff !important",
                      "& .MuiTableSortLabel-icon": {
                        color: "#fff !important",
                      },
                    },
                  },
                  "& .MuiTableSortLabel-icon": {
                    color: "#fff !important",
                  },
                  "& .MuiInputBase-input": {
                    color: "#fff !important",
                    textFillColor: "#fff !important",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#fff !important",
                    opacity: 0.8,
                  },
                  "& .MuiInput-underline:before": {
                    borderBottomColor: "#fff !important",
                  },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                    borderBottomColor: "#fff !important",
                  },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#fff !important",
                  },
                },
              }}
              muiTableBodyRowProps={({ row }) => ({
                sx: {
                  borderBottom: "1px solid #ccc",
                  backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
                },
              })}
              muiTableHeadCellFilterTextFieldProps={{
                sx: {
                  "& input": {
                    color: "#fff !important", // Change text color
                  },
                  "& .MuiInput-underline:before": {
                    borderBottomColor: "#fff !important", // Change underline color
                  },
                  "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                    borderBottomColor: "#fff !important", // Hover underline color
                  },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#fff !important", // Focused underline color
                  },
                },
              }}
              renderTopToolbarCustomActions={({ table }) => (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: { xs: "row", sm: "row" },
                    alignItems: "center",
                    width: "100%",
                    p: "0.5rem",
                    gap: { xs: "0.5rem", sm: "1rem" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {/* RIGHT SIDE BUTTON */}
                    <Tooltip title="Export data to excel sheet" arrow>
                      <Button
                        onClick={handleExportData}
                        style={{ background: "#1a73e829" }}
                        startIcon={<FileDownload />}
                        // startIcon={<IconTableExport />}
                      >
                        Export To Excel
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              )}
            />
          </Grid>
        </Grid>

        <Dialog
          open={breakdownOpen}
          onClose={() => setBreakdownOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, fontSize: "1.25rem" }}>
            Purchase Breakdown
          </DialogTitle>

          <DialogContent dividers sx={{ px: 3, py: 2 }}>
            <Typography
              sx={{ mb: 2, fontStyle: "italic", color: "text.secondary" }}
            >
              Detailed breakdown of purchase cost for this item
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                gap: 1,
                mb: 2,
                p: 1.5,
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 1,
              }}
            >
              {[
                ["Product Name", selectedData?.product_name],
                ["SKU", selectedData?.sku],
                ["Quantity", selectedData?.quantity],
                ["Invoice No.", selectedData?.order_number || selectedData?.order_receipt],
                [
                  "Order Date",
                  selectedData?.order_date
                    ? dayjs(selectedData.order_date).format("DD-MM-YYYY")
                    : "-",
                ],
                ["Store", selectedData?.store_name],
                ["Order Placed By", selectedData?.created_by_name],
                ["Order Status", selectedData?.order_status],
              ].map(([label, value]) => (
                <Box key={label} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ overflowWrap: "anywhere" }}
                  >
                    {value === null || value === undefined || value === ""
                      ? "-"
                      : value}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Table header */}
            <Box
              sx={{
                display: "flex",
                fontWeight: 600,
                borderBottom: "1px solid #ccc",
                pb: 1,
                mb: 1,
              }}
            >
              <Box sx={{ flex: 0.5 }}>S.No</Box>
              <Box sx={{ flex: 1 }}>Quantity</Box>
              <Box sx={{ flex: 1 }}>Buying Price (AED)</Box>
              <Box sx={{ flex: 1, textAlign: "right" }}>Subtotal (AED)</Box>
            </Box>

            {/* Transaction rows */}
            {breakdownData.transactions.map((tx, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  py: 0.5,
                  borderBottom:
                    index !== breakdownData.transactions.length - 1
                      ? "1px solid #eee"
                      : "none",
                  alignItems: "center",
                  bgcolor: index % 2 === 0 ? "grey.50" : "transparent", // alternating row color
                  px: 1,
                  borderRadius: 1,
                }}
              >
                <Box sx={{ flex: 0.5 }}>{index + 1}</Box>
                <Box sx={{ flex: 1 }}>{formatNumber(tx.quantity)}</Box>
                <Box sx={{ flex: 1 }}>
                  {formatAED(tx.buying_price)}
                </Box>
                <Box sx={{ flex: 1, textAlign: "right", fontWeight: 500 }}>
                  {formatAED(tx.quantity * tx.buying_price)}
                </Box>
              </Box>
            ))}

            {/* Total Row */}
            <Box
              sx={{
                display: "flex",
                fontWeight: 700,
                pt: 2,
                borderTop: "2px solid #ccc",
                mt: 2,
                alignItems: "center",
                bgcolor: "grey.100",
                px: 1,
                borderRadius: 1,
              }}
            >
              {/* Total label */}
              <Box sx={{ flex: 0.5 }}>Total</Box>

              {/* Total Quantity */}
              <Box sx={{ flex: 1 }}>
                {formatNumber(
                  breakdownData.transactions.reduce(
                    (sum, tx) => sum + tx.quantity,
                    0
                  )
                )}
              </Box>

              {/* Average Buying Price */}
              <Box
                sx={{ flex: 1, fontStyle: "italic", color: "text.secondary" }}
              >
                {/* Avg: {Number(selectedData.buying_price).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} */}
              </Box>

              {/* Total Subtotal */}
              <Box sx={{ flex: 1, textAlign: "right" }}>
                {formatAED(
                  breakdownData.transactions.reduce(
                    (sum, tx) => sum + tx.quantity * tx.buying_price,
                    0
                  )
                )}

              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setBreakdownOpen(false)}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={totalBreakdownOpen}
          onClose={() => setTotalBreakdownOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 600, fontSize: "1.25rem" }}>
            Grand Total Breakdown
          </DialogTitle>

          <DialogContent dividers sx={{ px: 3, py: 2 }}>
            <Typography
              sx={{ mb: 2, fontStyle: "italic", color: "text.secondary" }}
            >
              Detailed breakdown of total cost for this order
            </Typography>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Line Items
            </Typography>
            <Box
              sx={{
                mb: 2,
                border: "1px solid",
                borderColor: "grey.300",
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: hasLineItemDiscount
                    ? "minmax(0, 2fr) minmax(75px, 1fr) 55px minmax(75px, 1fr) minmax(80px, 1fr)"
                    : "minmax(0, 2fr) minmax(90px, 1fr) 70px minmax(90px, 1fr)",
                  gap: 1,
                  px: 1,
                  py: 0.75,
                  fontWeight: 700,
                  bgcolor: "grey.100",
                }}
              >
                <Box>Product / SKU</Box>
                <Box>Unit Price</Box>
                <Box>Qty</Box>
                {hasLineItemDiscount && <Box>Discount</Box>}
                <Box sx={{ textAlign: "right" }}>Subtotal</Box>
              </Box>
              {totalLineItems.map((item, index) => (
                <Box
                  key={`${item.sku || item.product_name || "item"}-${index}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: hasLineItemDiscount
                      ? "minmax(0, 2fr) minmax(75px, 1fr) 55px minmax(75px, 1fr) minmax(80px, 1fr)"
                      : "minmax(0, 2fr) minmax(90px, 1fr) 70px minmax(90px, 1fr)",
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    borderTop: "1px solid",
                    borderColor: "grey.200",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: "anywhere" }}>
                      {item.product_name || "-"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                      SKU: {item.sku || "-"}
                    </Typography>
                  </Box>
                  <Box>{formatAED(item.selling_price)}</Box>
                  <Box>{formatNumber(item.quantity || 0)}</Box>
                  {hasLineItemDiscount && (
                    <Box sx={{ color: "error.main" }}>
                      - {formatAED(item.discount_amount)}
                    </Box>
                  )}
                  <Box sx={{ textAlign: "right" }}>{formatAED(item.total_price)}</Box>
                </Box>
              ))}
            </Box>

            {/* Table header */}
            <Box
              sx={{
                display: "flex",
                fontWeight: 600,
                borderBottom: "1px solid #ccc",
                pb: 1,
                mb: 1,
              }}
            >
              <Box sx={{ flex: 1 }}>Label</Box>
              <Box sx={{ flex: 1, textAlign: "right" }}>Amount (AED)</Box>
            </Box>

            {/* Breakdown rows */}
            <Box sx={{ display: "flex", py: 0.5 }}>
              <Box sx={{ flex: 1 }}>Subtotal</Box>
              <Box sx={{ flex: 1, textAlign: "right" }}>
                {formatAED(totalData?.subtotal)}
              </Box>
            </Box>

            {Number(totalData?.discount_amount) > 0 && (
              <Box sx={{ display: "flex", py: 0.5 }}>
                <Box sx={{ flex: 1 }}>Discount (-)</Box>
                <Box sx={{ flex: 1, textAlign: "right", color: "error.main" }}>
                  - {formatAED(totalData?.discount_amount)}
                </Box>
              </Box>
            )}

            <Box sx={{ display: "flex", py: 0.5 }}>
              <Box sx={{ flex: 1 }}>Shipping (+)</Box>
              <Box sx={{ flex: 1, textAlign: "right" }}>
                +{" "}
                {formatAED(totalData?.shipping_amount)}
              </Box>
            </Box>

            <Box sx={{ display: "flex", py: 0.5 }}>
              <Box sx={{ flex: 1 }}>Tax (+)</Box>
              <Box sx={{ flex: 1, textAlign: "right" }}>
                +{" "}
                {formatAED(totalData?.tax_amount)}
              </Box>
            </Box>

            {/* Divider */}
            <Box sx={{ borderTop: "1px solid #ccc", my: 1 }} />

            {/* Grand Total */}
            <Box
              sx={{
                display: "flex",
                fontWeight: 700,
                py: 1,
                alignItems: "center",
                bgcolor: "grey.100",
                px: 1,
                borderRadius: 1,
              }}
            >
              <Box sx={{ flex: 1 }}>Grand Total</Box>
              <Box sx={{ flex: 1, textAlign: "right" }}>
                {formatAED(totalData?.grand_total)}
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setTotalBreakdownOpen(false)}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Layout>
    </>
  );
};

export default ProductSalesReports;
